import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Matter from '../../entities/matter';
import Invoice from '../../entities/invoice';
import Transaction from '../../entities/transaction';
import Task from '../../entities/task';
import Address from '../../entities/address';
import Answer from '../../entities/answer';
import Comment from '../../entities/comment';
import Deadline from '../../entities/deadline';
import Conflict from '../../entities/conflict';
import Discount from '../../entities/discount';
import Document from '../../entities/document';
import Expense from '../../entities/expense';
import Event from '../../entities/event';
import FlatFee from '../../entities/flatFee';
import MatterStage from '../../entities/matterStage';
import Participant from '../../entities/participant';
import RedFlag from '../../entities/redFlag';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteAddress, collectAddressDeleteImpact} from './addressDeps';
import {deleteAnswer, collectAnswerDeleteImpact} from './answerDeps';
import {deleteComment, collectCommentDeleteImpact} from './commentDeps';
import {deleteDeadline, collectDeadlineDeleteImpact} from './deadlineDeps';
import {deleteDiscount, collectDiscountDeleteImpact} from './discountDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
import {deleteExpense, collectExpenseDeleteImpact} from './expenseDeps';
import {deleteEvent, collectEventDeleteImpact} from './eventDeps';
import {deleteFlatFee, collectFlatFeeDeleteImpact} from './flatFeeDeps';
import {deleteMatterStage, collectMatterStageDeleteImpact} from './matterStageDeps';
import {deleteParticipant, collectParticipantDeleteImpact} from './participantDeps';
import {deleteRedFlag, collectRedFlagDeleteImpact} from './redFlagDeps';
export function canDeleteMatter(): boolean {
return canCascadeDelete();
}
export function collectMatterDeleteImpact(matter: Matter, context: DeleteImpactContext): void {
const key = 'matter:' + matter.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const invoiceMatterIdsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.matterIds?.includes(matter.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceMatterIdsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));
const transactionMatterIdsImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => transactionRef.matterIds?.includes(matter.id) && !(findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(transactionRef.id) || invoice.id === transactionRef.invoiceId)).length > 0));
transactionMatterIdsImpact.forEach(transactionRef => addDeleteImpact(context, 'transaction:' + transactionRef.id, 'transactions'));const transactionSourceTypeImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => transactionRef.sourceType === 'M' && transactionRef.source === matter.id && transactionRef.status !== 'D');
transactionSourceTypeImpact.forEach(transaction => addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions'));
const transactionTargetTypeImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => transactionRef.targetType === 'M' && transactionRef.target === matter.id && transactionRef.status !== 'D');
transactionTargetTypeImpact.forEach(transaction => addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions'));const taskTieInsImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.status !== 'D' && taskRef.hasOwnProperty('tieIns')).filter(taskRef => !!taskRef.tieIns?.some(f => {return 'Matter' === f.entity && f.id === matter.id;}));
taskTieInsImpact.forEach(task => addDeleteImpact(context, 'task:' + task.id, 'tasks'));workingRows<Address>('address', findAll<Address>('addresses')).filter(address => address.status !== 'D' && address.entityType === 'matter' && address.entityId === matter.id).forEach(address => {
if (context.visited.has('address:' + address.id)) return;
addDeleteImpact(context, 'address:' + address.id, 'addresses');
collectAddressDeleteImpact(address, context);
});
workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'matter' && answer.entityId === matter.id).forEach(answer => {
if (context.visited.has('answer:' + answer.id)) return;
addDeleteImpact(context, 'answer:' + answer.id, 'answers');
collectAnswerDeleteImpact(answer, context);
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'matter' && comment.entityId === matter.id).forEach(comment => {
if (context.visited.has('comment:' + comment.id)) return;
addDeleteImpact(context, 'comment:' + comment.id, 'comments');
collectCommentDeleteImpact(comment, context);
});
workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadline => deadline.status !== 'D' && deadline.matterId === matter.id).forEach(deadline => {
if (context.visited.has('deadline:' + deadline.id)) return;
addDeleteImpact(context, 'deadline:' + deadline.id, 'deadlines');
collectDeadlineDeleteImpact(deadline, context);
});
workingRows<Discount>('discount', findAll<Discount>('discounts')).filter(discount => discount.status !== 'D' && discount.entityType === 'matter' && discount.entityId === matter.id).forEach(discount => {
if (context.visited.has('discount:' + discount.id)) return;
addDeleteImpact(context, 'discount:' + discount.id, 'discounts');
collectDiscountDeleteImpact(discount, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'matter' && document.entityId === matter.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expense => expense.status !== 'D' && expense.entityType === 'matter' && expense.entityId === matter.id).forEach(expense => {
if (context.visited.has('expense:' + expense.id)) return;
addDeleteImpact(context, 'expense:' + expense.id, 'expenses');
collectExpenseDeleteImpact(expense, context);
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'matter' && event.entityId === matter.id).forEach(event => {
if (context.visited.has('event:' + event.id)) return;
addDeleteImpact(context, 'event:' + event.id, 'events');
collectEventDeleteImpact(event, context);
});
workingRows<FlatFee>('flatFee', findAll<FlatFee>('flatFees')).filter(flatFee => flatFee.status !== 'D' && flatFee.entityType === 'matter' && flatFee.entityId === matter.id).forEach(flatFee => {
if (context.visited.has('flatFee:' + flatFee.id)) return;
addDeleteImpact(context, 'flatFee:' + flatFee.id, 'flatFees');
collectFlatFeeDeleteImpact(flatFee, context);
});
workingRows<MatterStage>('matterStage', findAll<MatterStage>('matterStages')).filter(matterStage => matterStage.status !== 'D' && matterStage.matterId === matter.id).forEach(matterStage => {
if (context.visited.has('matterStage:' + matterStage.id)) return;
addDeleteImpact(context, 'matterStage:' + matterStage.id, 'matterStages');
collectMatterStageDeleteImpact(matterStage, context);
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'matter' && participant.entityId === matter.id).forEach(participant => {
if (context.visited.has('participant:' + participant.id)) return;
addDeleteImpact(context, 'participant:' + participant.id, 'participants');
collectParticipantDeleteImpact(participant, context);
});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'matter' && redFlag.entityId === matter.id).forEach(redFlag => {
if (context.visited.has('redFlag:' + redFlag.id)) return;
addDeleteImpact(context, 'redFlag:' + redFlag.id, 'redFlags');
collectRedFlagDeleteImpact(redFlag, context);
});
}
export function deleteImpactMatter(matter: Matter): DeleteImpact {
const context = deleteImpactContext();
collectMatterDeleteImpact(matter, context);
return deleteImpactCounts(context);
}
export function deleteMatter(matter: Matter, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteMatter()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectMatterDeleteImpact(matter, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || matter.id};
}
const key = 'matter:' + matter.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Address>('address', findAll<Address>('addresses')).filter(address => address.status !== 'D' && address.entityType === 'matter' && address.entityId === matter.id).forEach(address => {deleteAddress(address, mergeId, cascade);});
workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'matter' && answer.entityId === matter.id).forEach(answer => {deleteAnswer(answer, mergeId, cascade);});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'matter' && comment.entityId === matter.id).forEach(comment => {deleteComment(comment, mergeId, cascade);});
workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadline => deadline.status !== 'D' && deadline.matterId === matter.id).forEach(deadline => {deleteDeadline(deadline, mergeId, cascade);});
workingRows<Discount>('discount', findAll<Discount>('discounts')).filter(discount => discount.status !== 'D' && discount.entityType === 'matter' && discount.entityId === matter.id).forEach(discount => {deleteDiscount(discount, mergeId, cascade);});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'matter' && document.entityId === matter.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expense => expense.status !== 'D' && expense.entityType === 'matter' && expense.entityId === matter.id).forEach(expense => {deleteExpense(expense, mergeId, cascade);});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'matter' && event.entityId === matter.id).forEach(event => {deleteEvent(event, mergeId, cascade);});
workingRows<FlatFee>('flatFee', findAll<FlatFee>('flatFees')).filter(flatFee => flatFee.status !== 'D' && flatFee.entityType === 'matter' && flatFee.entityId === matter.id).forEach(flatFee => {deleteFlatFee(flatFee, mergeId, cascade);});
workingRows<MatterStage>('matterStage', findAll<MatterStage>('matterStages')).filter(matterStage => matterStage.status !== 'D' && matterStage.matterId === matter.id).forEach(matterStage => {deleteMatterStage(matterStage, mergeId, cascade);});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'matter' && participant.entityId === matter.id).forEach(participant => {deleteParticipant(participant, mergeId, cascade);});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'matter' && redFlag.entityId === matter.id).forEach(redFlag => {deleteRedFlag(redFlag, mergeId, cascade);});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('matterId', matter.id)]).filter(invoiceTemp => invoiceTemp.matterIds?.includes(matter.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.matterIds = _invoice.matterIds?.filter(id => id !== matter.id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});
workingRows<Transaction>('transaction', [...store.getState().transactions.getMappingFor('matterId', matter.id)]).filter(transactionTemp => (transactionTemp.matterIds?.includes(matter.id) && transactionTemp.matterIds.some(other => other !== matter.id))).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
if (_transaction.matterIds?.some(other => other !== matter.id)) _transaction.matterIds = _transaction.matterIds.filter(id => id !== matter.id);

_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => transactionTemp.sourceType === 'M' && transactionTemp.source === matter.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.source = null;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => transactionTemp.targetType === 'M' && transactionTemp.target === matter.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.target = null;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Matter' === f.entity && f.id === matter.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].filter(f => !('Matter' === f.entity && f.id === matter.id));
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
const deleted = matter.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.MatterActions.removeMatterQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceMatter(matter: Matter, replaceWith: Matter, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = matter.id;}
const _work = workingSet();
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('matterId', matter.id)]).filter(invoiceTemp => invoiceTemp.matterIds?.includes(matter.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.matterIds = _invoice.matterIds?.map(id => (id === matter.id) ? replaceWith.id : id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});
workingRows<Transaction>('transaction', [...store.getState().transactions.getMappingFor('matterId', matter.id)]).filter(transactionTemp => transactionTemp.matterIds?.includes(matter.id)).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_transaction.matterIds = _transaction.matterIds?.map(id => (id === matter.id) ? replaceWith.id : id) || [];

_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => transactionTemp.sourceType === 'M' && transactionTemp.source === matter.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.source = replaceWith.id;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => transactionTemp.targetType === 'M' && transactionTemp.target === matter.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.target = replaceWith.id;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => transactionTemp.status !== 'D' && transactionTemp.hasOwnProperty('paymentDistribution')).filter(transactionTemp => !!transactionTemp.paymentDistribution?.some(item => item.matterId === matter.id)).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
if (!!_transaction.paymentDistribution) {_transaction.paymentDistribution = [..._transaction.paymentDistribution].map(f => {if (f.matterId === matter.id) {const _f = {...f}; _f.matterId = replaceWith.id; return _f;} else return f;});} else {_transaction.paymentDistribution = [];}
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Matter' === f.entity && f.id === matter.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].map(f => {if ('Matter' === f.entity && f.id === matter.id) {const _f = {...f}; _f.id = replaceWith.id; return _f;} else return f;});
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'matter' && comment.entityId === matter.id).forEach(commentTemp => {
const _comment = ((_work.get('comment:' + commentTemp.id) ?? commentTemp) as Comment).clone();
if (_comment.status === 'D') return;
_comment.entityId = replaceWith.id;
_comment.status = 'U';
_comment.addedOn = newHRTimestamp();
_comment.addedBy = getCurrentUserId();
_comment.importSource = targetImportSource;
_comment.importId = mergeId;
_work.set('comment:' + _comment.id, _comment);
store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
});
workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadline => deadline.status !== 'D' && deadline.matterId === matter.id).forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.matterId = replaceWith.id;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
_work.set('deadline:' + _deadline.id, _deadline);
store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
});
workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflict => conflict.status !== 'D' && conflict.entityType === 'matter' && conflict.entityId === matter.id).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.entityId = replaceWith.id;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_work.set('conflict:' + _conflict.id, _conflict);
store.dispatch(Actions.ConflictActions.saveConflictQueue(_conflict));
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'matter' && document.entityId === matter.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expense => expense.status !== 'D' && expense.entityType === 'matter' && expense.entityId === matter.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.entityId = replaceWith.id;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
_work.set('expense:' + _expense.id, _expense);
store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'matter' && event.entityId === matter.id).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.entityId = replaceWith.id;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
_work.set('event:' + _event.id, _event);
store.dispatch(Actions.EventActions.saveEventQueue(_event));
});
workingRows<FlatFee>('flatFee', findAll<FlatFee>('flatFees')).filter(flatFee => flatFee.status !== 'D' && flatFee.entityType === 'matter' && flatFee.entityId === matter.id).forEach(flatFeeTemp => {
const _flatFee = ((_work.get('flatFee:' + flatFeeTemp.id) ?? flatFeeTemp) as FlatFee).clone();
if (_flatFee.status === 'D') return;
_flatFee.entityId = replaceWith.id;
_flatFee.status = 'U';
_flatFee.addedOn = newHRTimestamp();
_flatFee.addedBy = getCurrentUserId();
_flatFee.importSource = targetImportSource;
_flatFee.importId = mergeId;
_work.set('flatFee:' + _flatFee.id, _flatFee);
store.dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee));
});
workingRows<MatterStage>('matterStage', findAll<MatterStage>('matterStages')).filter(matterStage => matterStage.status !== 'D' && matterStage.matterId === matter.id).forEach(matterStageTemp => {
const _matterStage = ((_work.get('matterStage:' + matterStageTemp.id) ?? matterStageTemp) as MatterStage).clone();
if (_matterStage.status === 'D') return;
_matterStage.matterId = replaceWith.id;
_matterStage.status = 'U';
_matterStage.addedOn = newHRTimestamp();
_matterStage.addedBy = getCurrentUserId();
_matterStage.importSource = targetImportSource;
_matterStage.importId = mergeId;
_work.set('matterStage:' + _matterStage.id, _matterStage);
store.dispatch(Actions.MatterStageActions.saveMatterStageQueue(_matterStage));
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'matter' && participant.entityId === matter.id).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.entityId = replaceWith.id;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
_work.set('participant:' + _participant.id, _participant);
store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'matter' && redFlag.entityId === matter.id).forEach(redFlagTemp => {
const _redFlag = ((_work.get('redFlag:' + redFlagTemp.id) ?? redFlagTemp) as RedFlag).clone();
if (_redFlag.status === 'D') return;
_redFlag.entityId = replaceWith.id;
_redFlag.status = 'U';
_redFlag.addedOn = newHRTimestamp();
_redFlag.addedBy = getCurrentUserId();
_redFlag.importSource = targetImportSource;
_redFlag.importId = mergeId;
_work.set('redFlag:' + _redFlag.id, _redFlag);
store.dispatch(Actions.RedFlagActions.saveRedFlagQueue(_redFlag));
});
return matter;
}
